import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Moon, Sun, Menu, X } from "lucide-react";
import {
  SignInButton,
  SignedIn,
  SignedOut,
  UserButton,
} from "@clerk/clerk-react";
import { useTheme } from "@/context/ThemeContext";

const NAV_LINKS = [
  { to: "/",           label: "Home" },
  { to: "/demo",       label: "Demo" },
  { to: "/pricing",    label: "Pricing" },
  { to: "/about",      label: "About" },
  { to: "/contact",    label: "Contact" },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu whenever the route changes
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  const logoContent = (
    <>
      <img src="/images/easyreports_logo.png" alt="EasyReports logo" style={{ height: 28, width: 28, objectFit: "contain" }} />
      EasyReports
    </>
  );

  const navLinkClass = (path) =>
    `text-sm font-medium text-muted-foreground no-underline px-3 py-[0.4rem] rounded-[6px] transition-colors hover:text-foreground hover:bg-accent${
      pathname === path ? " text-foreground bg-accent" : ""
    }`;

  const mobileLinkClass = (path) =>
    `text-base font-medium text-muted-foreground no-underline px-3 py-2.5 rounded-[8px] transition-colors hover:text-foreground hover:bg-accent${
      pathname === path ? " text-foreground bg-accent" : ""
    }`;

  return (
    <header
      className={`sticky top-0 z-50 bg-background border-b border-border transition-[background,box-shadow,border-color] duration-200${
        scrolled
          ? " bg-background/95 backdrop-blur-[8px] shadow-[0_1px_16px_rgba(0,0,0,0.06)]"
          : ""
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-8 h-[60px]">
        <SignedOut>
          <Link
            to="/"
            className="flex items-center gap-2 font-display text-[1.1rem] text-foreground no-underline tracking-[-0.01em]"
          >
            {logoContent}
          </Link>
        </SignedOut>
        <SignedIn>
          <Link
            to="/dashboard"
            className="flex items-center gap-2 font-display text-[1.1rem] text-foreground no-underline tracking-[-0.01em]"
          >
            {logoContent}
          </Link>
        </SignedIn>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-3">
          <nav className="flex items-center gap-1 pr-3 border-r border-border">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className={navLinkClass(l.to)}>
                {l.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              title={theme === "light" ? "Dark mode" : "Light mode"}
              className="ml-1 w-8 h-8 rounded-[6px] flex items-center justify-center text-muted-foreground bg-transparent border-none cursor-pointer transition-colors hover:text-foreground hover:bg-accent"
            >
              {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
            </button>
          </nav>
          <div className="flex items-center gap-3">
            <SignedOut>
              <SignInButton mode="modal" afterSignInUrl="/dashboard" afterSignUpUrl="/dashboard">
                <button className="text-sm font-medium px-4 py-[0.45rem] rounded-[7px] border border-border bg-transparent text-foreground cursor-pointer transition-[border-color,background] hover:border-foreground hover:bg-accent">
                  Sign in
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <Link to="/dashboard">
                <button className="text-sm font-medium px-4 py-[0.45rem] rounded-[7px] bg-brand-800 text-white cursor-pointer transition-colors hover:bg-brand-700">
                  Dashboard
                </button>
              </Link>
              <UserButton afterSignOutUrl="/" />
            </SignedIn>
          </div>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          className="md:hidden w-10 h-10 -mr-2 rounded-[8px] flex items-center justify-center text-foreground bg-transparent border-none cursor-pointer transition-colors hover:bg-accent"
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div className="md:hidden border-t border-border bg-background shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
          <nav className="flex flex-col px-4 py-3 gap-1">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className={mobileLinkClass(l.to)}>
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="border-t border-border px-4 py-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground px-3 py-2 rounded-[8px] bg-transparent border-none cursor-pointer transition-colors hover:text-foreground hover:bg-accent"
            >
              {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
              {theme === "light" ? "Dark mode" : "Light mode"}
            </button>
            <SignedOut>
              <SignInButton mode="modal" afterSignInUrl="/dashboard" afterSignUpUrl="/dashboard">
                <button className="text-sm font-medium px-4 py-2 rounded-[7px] bg-brand-800 text-white cursor-pointer transition-colors hover:bg-brand-700">
                  Sign in
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <div className="flex items-center gap-3">
                <Link to="/dashboard">
                  <button className="text-sm font-medium px-4 py-2 rounded-[7px] bg-brand-800 text-white cursor-pointer transition-colors hover:bg-brand-700">
                    Dashboard
                  </button>
                </Link>
                <UserButton afterSignOutUrl="/" />
              </div>
            </SignedIn>
          </div>
        </div>
      )}
    </header>
  );
}
